import { useState, useEffect, useRef } from 'react';
import { Heart, Download, Share } from 'lucide-react';

const FloatingHearts = () => {
  const hearts = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    size: Math.random() * 20 + 10,
    duration: Math.random() * 15 + 10,
    delay: Math.random() * 10,
    opacity: Math.random() * 0.5 + 0.3
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {hearts.map(heart => (
        <div 
          key={heart.id}
          className="absolute animate-float"
          style={{
            left: `${heart.left}%`,
            animationDuration: `${heart.duration}s`,
            animationDelay: `${heart.delay}s`,
            opacity: heart.opacity
          }}
        >
          <Heart 
            size={heart.size} 
            color="#FF5A5A" 
            fill="#FF5A5A" 
            className="animate-pulse" 
            style={{animationDuration: `${heart.duration / 2}s`}} 
          />
        </div>
      ))}
    </div>
  );
};

const LoveLanguageQuiz = () => {
  const [quizState, setQuizState] = useState('start');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [randomImageIndex, setRandomImageIndex] = useState(Math.floor(Math.random() * 5));

  useEffect(() => {
    setAnimateIn(true);
  }, [currentQuestion, quizState]);

  const questions = [
    {
      text: "What makes you feel most appreciated?",
      options: [
        "Receiving a thoughtful gift",
        "Spending quality time together",
        "Hearing words of affirmation",
        "Getting a helping hand",
        "Physical touch and closeness"
      ]
    },
    {
      text: "What hurts your feelings the most?",
      options: [
        "When someone forgets a special occasion",
        "When someone cancels plans with you",
        "When someone doesn't acknowledge your efforts",
        "When someone doesn't offer help when you need it",
        "When someone avoids physical contact with you"
      ]
    },
    {
      text: "How do you typically express affection?",
      options: [
        "Giving meaningful gifts",
        "Planning special activities",
        "Saying how much you care",
        "Doing helpful things without being asked",
        "Through hugs, holding hands, or other physical touches"
      ]
    },
    {
      text: "What do you remember most from precious moments?",
      options: [
        "The special items received or exchanged",
        "The experiences and memories created together",
        "The meaningful conversations and words shared",
        "How someone went out of their way to help you",
        "The comfort of being close to someone special"
      ]
    },
    {
      text: "What type of gesture would mean the most to you?",
      options: [
        "A surprise gift that shows they know you well",
        "A day spent doing your favorite activities together",
        "A heartfelt letter expressing their feelings",
        "Them taking care of a task you've been dreading",
        "A warm embrace when you need it most"
      ]
    },
    {
      text: "How do you prefer to celebrate achievements?",
      options: [
        "With a meaningful gift to commemorate it",
        "By going somewhere special together",
        "By hearing praise and recognition",
        "Having someone help make the celebration happen",
        "With physical celebration like high-fives or hugs"
      ]
    },
    {
      text: "What makes you feel most connected to others?",
      options: [
        "Exchanging thoughtful gifts",
        "Having deep conversations and shared experiences",
        "Verbal expressions of understanding and care",
        "Working together to accomplish something",
        "Physical presence and affectionate touches"
      ]
    },
  ];

  const loveLanguages = [
    {
      name: "Receiving Gifts",
      description: "You value thoughtful gifts and tokens of affection. To you, a meaningful gift shows that someone truly knows you and appreciates having you in their life.",
      color: "#FF5A5A"
    },
    {
      name: "Quality Time",
      description: "You cherish undivided attention and shared experiences. Being fully present with someone special is your way of showing and feeling love.",
      color: "#FF7D7D"
    },
    {
      name: "Words of Affirmation",
      description: "You thrive on verbal expressions of love and appreciation. Kind words and encouragement speak deeply to your heart.",
      color: "#FF9E9E"
    },
    {
      name: "Acts of Service",
      description: "You feel loved when someone goes out of their way to make your life easier. Actions speak louder than words to you.",
      color: "#FFC0C0"
    },
    {
      name: "Physical Touch",
      description: "You feel connected through physical affection. Hugs, hand-holding, and thoughtful touches make you feel secure and loved.",
      color: "#FFE0E0"
    }
  ];

  const handleAnswer = (optionIndex) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate results
      const counts = [0, 0, 0, 0, 0];
      newAnswers.forEach((answer) => {
        counts[answer]++;
      });
      
      // Find the highest score
      let maxIndex = 0;
      for (let i = 1; i < counts.length; i++) {
        if (counts[i] > counts[maxIndex]) {
          maxIndex = i;
        }
      }
      
      setResult(loveLanguages[maxIndex]);
      setRandomImageIndex(Math.floor(Math.random() * 5));
      setQuizState('result');
    }
  };

  const handleRestart = () => {
    setQuizState('start');
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
    setRandomImageIndex(Math.floor(Math.random() * 5));
    };
    const saveImage = () => {
        // Get a reference to the result card element
        const resultCard = document.getElementById('result-card');
        
        if (!resultCard) {
            alert("Could not find the result card to save as an image.");
            return;
        }
        
        // Use html2canvas for more reliable DOM-to-image conversion
        html2canvas(resultCard, { 
            useCORS: true,  // Enable cross-origin rendering
            scale: 2,       // Increase resolution
            logging: false  // Disable logging
        }).then(canvas => {
            // Convert canvas to data URL
            const dataUrl = canvas.toDataURL('image/png');
            
            // Create download link
            const link = document.createElement('a');
            link.download = `love-language-${result.name.replace(/\s+/g, '-').toLowerCase()}.png`;
            link.href = dataUrl;
            link.click();
        }).catch(error => {
            console.error("Error creating image:", error);
            alert("There was an error creating your image. You can take a screenshot instead!");
        });
    };
    
    // Ensure html2canvas is loaded
    if (typeof html2canvas === 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = () => {
            console.log('html2canvas library loaded successfully');
        };
        script.onerror = () => {
            console.error('Failed to load html2canvas library');
        };
        document.head.appendChild(script);
    }
  const shareOnTwitter = () => {
    // Placeholder for Twitter sharing
    const text = `My love language is ${result.name}! Take the quiz yourself and support TXT's comeback on May 2nd! #TXT #Comeback`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-b from-red-50 to-white text-gray-800 p-4 relative">
      <FloatingHearts />
      
      {/* Comeback Banner */}
      <div className="fixed top-4 right-4 bg-red-500 p-3 rounded-xl shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300 z-10">
        <p className="text-white font-bold text-lg tracking-wide animate-pulse">
          TXT comeback MAY ❤️ 2nd
        </p>
      </div>

      <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl overflow-hidden my-8 border-4 border-red-200 relative z-10">
      {quizState === 'start' && (
  <div className="flex flex-col items-center p-8 text-center relative overflow-hidden bg-yellow"> {/* Pale yellow background */}
    <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-r from-red-200 via-red-100 to-red-200 transform -skew-y-3"></div>
    
    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-500 mb-6 mt-8 relative z-10">
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">L</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">o</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">v</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">e</span>
      <span className="inline-block"> </span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">L</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">a</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">n</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">g</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">u</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">a</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">g</span>
      <span className="inline-block transform hover:scale-105 transition-transform duration-300">e</span>
    </h1>
    
    <div className="relative">
      <div className="absolute -left-4 -top-4 w-12 h-12 text-red-300 transform rotate-12">
        <Heart size={30} fill="#FCA5A5" />
      </div>
      <div className="absolute -right-4 -top-4 w-12 h-12 text-red-300 transform -rotate-12">
        <Heart size={30} fill="#FCA5A5" />
      </div>
      
      <div className="bg-gradient-to-br from-red-100 to-red-50 w-full h-64 mb-6 rounded-2xl flex items-center justify-center border-2 border-red-200 shadow-inner overflow-hidden relative">
        <img src="/images/txt.jpg" alt="Love Language Quiz" className="w-full h-full object-cover" />
        <div className="absolute bottom-3 right-3">
          <Heart size={24} color="#FF5A5A" fill="#FF5A5A" />
        </div>
        <div className="absolute top-3 left-3">
          <Heart size={24} color="#FF5A5A" fill="#FF5A5A" />
        </div>
      </div>
      
      <div className="absolute -left-4 -bottom-4 w-12 h-12 text-red-300 transform -rotate-12">
        <Heart size={30} fill="#FCA5A5" />
      </div>
      <div className="absolute -right-4 -bottom-4 w-12 h-12 text-red-300 transform rotate-12">
        <Heart size={30} fill="#FCA5A5" />
      </div>
    </div>
    
    <p className="mb-8 text-gray-600 font-medium leading-relaxed px-4">
      Discover your love language and find out how you express and receive affection! 
      Take this adorable quiz to see what makes your heart flutter! ✨
    </p>
    
    <button 
      onClick={() => setQuizState('quiz')}
      className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold py-4 px-10 rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:shadow-xl"
    >
      <span className="flex items-center">
        <Heart size={18} className="mr-2" fill="white" />
        Start Quiz
        <Heart size={18} className="ml-2" fill="white" />
      </span>
    </button>
  </div>
)}

        {quizState === 'quiz' && (
          <div className={`p-8 transition-opacity duration-500 ${animateIn ? 'opacity-100' : 'opacity-0'}`} 
               onAnimationEnd={() => currentQuestion > 0 && setAnimateIn(false)}>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm font-medium text-red-500 bg-red-50 py-1 px-3 rounded-full">
                Question {currentQuestion + 1} of {questions.length}
              </div>
              <div className="flex">
                {Array.from({ length: questions.length }).map((_, idx) => (
                  <div 
                    key={idx} 
                    className={`w-2 h-2 rounded-full mx-1 ${idx <= currentQuestion ? 'bg-red-500' : 'bg-gray-200'}`}
                  ></div>
                ))}
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-red-400 to-red-600 h-2 rounded-full transition-all duration-700 ease-out" 
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
            
            <div className="relative mb-8">
              <Heart className="absolute -left-2 -top-2 text-red-200" size={20} fill="#FECACA" />
              <h2 className="text-2xl font-bold mb-6 text-gray-700 pl-4">
                {questions[currentQuestion].text}
              </h2>
              <Heart className="absolute -right-2 bottom-0 text-red-200" size={20} fill="#FECACA" />
            </div>
            
            <div className="space-y-4">
              {questions[currentQuestion].options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  className="w-full text-left p-5 bg-gradient-to-r from-red-50 to-white hover:from-red-100 hover:to-red-50 rounded-xl transition duration-300 border-2 border-red-100 hover:border-red-300 shadow-sm hover:shadow-md transform hover:-translate-y-1 flex items-center group relative overflow-hidden"
                >
                  <span className="absolute right-0 top-0 h-full w-12 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Heart size={20} className="text-red-400" fill="#FC8181" />
                  </span>
                  <span className="pr-8">{option}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {quizState === 'result' && result && (
          <div className="p-8 text-center">
            <div className="relative mb-6">
              <div className="absolute -left-3 -top-3">
                <Heart size={30} fill="#FECACA" />
              </div>
              <div className="absolute -right-3 -top-3">
                <Heart size={30} fill="#FECACA" />
              </div>
              <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-500 mb-4">
                Your Love Language is:
              </h2>
            </div>
            
            <div className="relative">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <Heart size={40} fill="#FCA5A5" className="animate-pulse" />
              </div>
              
              <div id="result-card" className="bg-gradient-to-br from-white to-red-50 p-8 rounded-2xl mb-8 border-4 border-red-200 shadow-xl" 
                   style={{ backgroundColor: result.color + '40' }}>
                <div className="relative mb-4">
                  <div className="absolute -top-16 -left-8 rotate-12 opacity-20">
                    <Heart size={80} fill="#FCA5A5" />
                  </div>
                  <div className="absolute -bottom-16 -right-8 -rotate-12 opacity-20">
                    <Heart size={80} fill="#FCA5A5" />
                  </div>
                  
                  <div className="bg-white w-full h-56 mb-6 rounded-xl flex items-center justify-center shadow-inner border-2 border-red-100 overflow-hidden relative">
                    <img 
                      src={`/images/loveLang${randomImageIndex + 1}.png`} 
                      alt={result.name}
                      className="w-full h-full object-cover" 
                    />
                    <div className="absolute bottom-3 right-3">
                      <Heart size={24} color="#FF5A5A" fill="#FF5A5A" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <Heart size={24} color="#FF5A5A" fill="#FF5A5A" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-red-600 mb-3 relative">
                    <span className="relative inline-block">
                      {result.name}
                      <span className="absolute bottom-0 left-0 w-full h-1 bg-red-300 rounded-full"></span>
                    </span>
                  </h3>
                  
                  <p className="text-gray-700 mb-4 leading-relaxed">{result.description}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center space-x-6 mb-8">
              <button 
                onClick={saveImage}
                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 transform hover:scale-105 flex items-center"
              >
                <Download className="h-5 w-5 mr-2" />
                Save Image
              </button>
              <button 
                onClick={shareOnTwitter}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 transform hover:scale-105 flex items-center"
              >
                <Share className="h-5 w-5 mr-2" />
                Share on X
              </button>
            </div>
            
            <div className="bg-gradient-to-r from-red-100 to-pink-100 p-6 rounded-xl mb-8 border-2 border-red-200 shadow-md transform rotate-1 hover:rotate-0 transition-transform duration-300">
              <p className="text-red-600 font-bold text-lg flex items-center justify-center">
                <Heart size={20} className="mr-2 animate-pulse" fill="#EF4444" />
                Please support TXT's comeback on May 2nd!
                <Heart size={20} className="ml-2 animate-pulse" fill="#EF4444" />
              </p>
            </div>
            
            <button 
              onClick={handleRestart}
              className="text-red-500 hover:text-red-700 font-medium flex items-center mx-auto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Take the quiz again
            </button>
          </div>
        )}
      </div>
      
      <footer className="text-center text-red-400 font-medium mt-auto mb-4 p-4 relative">
        <div className="absolute left-1/4 -top-6 opacity-30">
          <Heart size={24} fill="#FCA5A5" />
        </div>
        <div className="absolute right-1/4 -top-6 opacity-30">
          <Heart size={24} fill="#FCA5A5" />
        </div>
        Made with L❤️VE by Cheonsa
      </footer>
    </div>
  );
};

export default LoveLanguageQuiz;